/**
 * gamba-card-subscription.js — hidrata la fila "SUSCRIBITE / a precio gamba" de
 * `snippets/gamba-product-card.liquid` con el descuento real de la app.
 *
 * POR QUÉ EXISTE. Liquid pinta la fila con el fallback horneado (10%), porque no
 * puede llamar a un endpoint. Gamba edita ese número en el backoffice
 * (Apps → Gamba Suscripciones → Configuración) y la tienda tiene que reflejarlo
 * SIN DEPLOY. Es el mismo contrato que ya cumplen el panel de la PDP
 * (`gamba-purchase-mode.js`) y el botón del carrito (`gamba-cart-subscribe.js`);
 * la card era la única de las tres que no lo cumplía.
 *
 * Hasta el 2026-09-10 la fila ni siquiera se emitía: su condición era que la
 * variante tuviera un selling plan recurrente, y esta tienda tiene
 * `sellingPlanGroups: []` porque la suscripción corre sobre MercadoPago con la
 * app propia. Era código muerto.
 *
 * LA CUENTA ES LA DE LIQUID, NO OTRA. `trunc(x·(100−p)/100)` en enteros, igual
 * que `gamba-product-card.liquid` y que `gamba-cart-subscribe.liquid`. Si el
 * módulo redondeara distinto al primer pintado, la card cambiaría de precio sola
 * al hidratar aunque el descuento no hubiera cambiado — un parpadeo que se lee
 * como bug de precio, que es la peor clase de bug que puede tener una grilla.
 *
 * NO ES UN CUSTOM ELEMENT. La card no tiene ciclo de vida propio: es markup
 * estático dentro de `<product-component>`. Lo único que necesita es correr
 * cuando aparecen cards nuevas, y en esta tienda aparecen por el Section
 * Rendering API (los facets de collection). De ahí el observer.
 */

import { formatMoney } from '@theme/money-formatting';

/** Las cards que todavía no se hidrataron con ESTE porcentaje. */
const HYDRATED = new WeakMap();

/**
 * El precio con descuento, en unidades menores, con la MISMA truncada que Liquid.
 *
 * @param {number} unitPrice - precio de lista en unidades menores (centavos)
 * @param {number} percent - 0..100
 * @returns {number}
 */
function discounted(unitPrice, percent) {
  return Math.trunc((unitPrice * (100 - percent)) / 100);
}

/**
 * Aplica un porcentaje a una card.
 *
 * @param {HTMLElement} row
 * @param {number} percent
 */
function apply(row, percent) {
  // Ya está en este número: no se toca el DOM. Evita repintar la grilla entera
  // cuando el observer se dispara por otra cosa.
  if (HYDRATED.get(row) === percent) return;

  const unitPrice = Number.parseInt(row.dataset.unitPrice ?? '', 10);
  if (!Number.isFinite(unitPrice) || unitPrice <= 0) return;

  const priceEl = row.querySelector('[data-gamba-card-sub-price]');
  const savingEl = row.querySelector('[data-gamba-card-sub-saving]');

  if (priceEl) {
    priceEl.textContent = formatMoney(
      discounted(unitPrice, percent),
      row.dataset.moneyFormat || '{{amount}}',
      row.dataset.currency ?? ''
    );
  }

  if (savingEl) {
    // Un descuento en 0 no se dice: "Ahorrá 0%" es peor que no decir nada. La
    // fila entera sigue visible porque el precio de suscripción sigue siendo
    // información aunque no haya ahorro.
    if (percent > 0) {
      const template = row.dataset.savingTemplate || '';
      // El centinela va sin llaves: ver el comentario del snippet.
      savingEl.textContent = template.replace('__PERCENT__', String(percent));
      savingEl.hidden = false;
    } else {
      savingEl.hidden = true;
    }
  }

  HYDRATED.set(row, percent);
}

/**
 * @param {ParentNode} root
 * @param {number} percent
 */
function hydrate(root, percent) {
  const rows = root.querySelectorAll?.('[data-gamba-card-sub]');
  if (rows) rows.forEach((row) => apply(/** @type {HTMLElement} */ (row), percent));
}

/**
 * El porcentaje vigente. Arranca en el que ya pintó Liquid y lo pisa la app.
 * Se guarda a nivel de módulo para que las cards que llegan DESPUÉS del `load()`
 * —las del Section Rendering API— se hidraten con el valor bueno y no vuelvan a
 * pedirlo.
 */
let currentPercent = null;

async function start() {
  const api = window.GambaSubscriptionConfig;
  // El `?.` no es adorno: sin el `<script>` del config, la card se queda con lo
  // que pintó Liquid en vez de tirar TypeError y romper la grilla.
  const config = await api?.load?.();
  currentPercent = config?.discountPercent ?? null;
  if (currentPercent === null) return;

  hydrate(document, currentPercent);

  /*
   * Las cards que llegan por el Section Rendering API (facets de collection,
   * paginación) nacen con el fallback de Liquid. El observer las alcanza.
   * Se mira `childList` en subtree y se hidrata sólo lo agregado, no el
   * documento entero, para no recorrer la grilla en cada mutación.
   */
  new MutationObserver((mutations) => {
    if (currentPercent === null) return;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = /** @type {HTMLElement} */ (node);
        if (el.matches?.('[data-gamba-card-sub]')) apply(el, currentPercent);
        else hydrate(el, currentPercent);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

start();
